import { useQuery } from '@apollo/client';
import { useRouter } from '@application/utils';
import { graphql } from '@gql/gql';
import type { BreederGetBreederQuery, BreederGetBreederQueryVariables } from '@gql/graphql';
import { DogBreederApplicationStatus } from '@gql/graphql';
import { useMemo } from 'react';

export const BREEDER_GET_BREEDER = graphql(`
  query BreederGetBreeder(
    $where: DogBreederWhereInput!
    $take: Int
    $littersWhere: LitterWhereInput
    $applicationsWhere: DogBreederApplicationWhereInput!
    $breedCardInfoTranslationsWhere: DogBreedTranslationWhereInput!
    $litterCardInfoImgsTake: Int!
    $litterCardInfoTranslationsWhere: LitterTranslationWhereInput!
    $litterCardInfoBreedTranslationsWhere: DogBreedTranslationWhereInput!
  ) {
    findManyDogBreeder(where: $where, take: $take) {
      id
      displayName
      whatsappNumber
      certified
      public
      dogsPedigree
      puppiesBirthPlace
      agreementNumber
      websiteUrl
      certificationFile
      level
      spokenLanguages
      breedingName
      profileImg
      lastConnectedType
      imgs {
        id
        small
        medium
        type
        large
      }
      translations {
        id
        title
        description
        seoKeywords
        dogsActivities
        puppiesLivingPlace
        dogsLivingPlace
        health
        breederDogsSelection
        puppiesTraining
        url
        language
      }

      account {
        id
        connectedAt
        firstName
        lastName
        language
        address {
          id
          zip
          city
          country
          street
          region
        }
      }

      reviews {
        accountId
        id
        globalGrade
      }

      breederBreeds {
        id
        dogBreed {
          id
          isSubBreed
          baseBreedName
          ...BreedCardInfo
        }
      }

      litters(where: $littersWhere) {
        ...LitterCardInfo
      }

      applications(where: $applicationsWhere) {
        id
      }
    }
  }
`);

export const useGetBreeder = (id: string) => {
  const { language } = useRouter();

  const variables: BreederGetBreederQueryVariables = useMemo(() => {
    // coming from a redirect, check for id in the URL
    const redirected = !Number.isNaN(Number(id.split('-').pop()));
    let dbId;
    if (redirected) {
      dbId = Number(id.split('-').pop());
    }

    return {
      take: 1,
      where: {
        AND: [
          {
            OR: [{ translations: { some: { url: { equals: id } } } }, { id: { equals: dbId } }],
          },
        ],
      },
      reviewsWhere: {},
      breedCardInfoTranslationsWhere: { language: { equals: language } },
      litterCardInfoImgsTake: 1,
      litterCardInfoTranslationsWhere: { language: { equals: language } },
      litterCardInfoBreedTranslationsWhere: { language: { equals: language } },
      littersWhere: {
        AND: [
          { OR: [{ archived: { equals: false } }, { archived: { equals: null } }] },
          { breeder: { is: { certified: { equals: true }, public: { equals: true } } } },
          { adminValidated: { equals: true } },
        ],
      },
      applicationsWhere: { status: { equals: DogBreederApplicationStatus.DONE } },
    } as BreederGetBreederQueryVariables;
  }, [id, language]);

  const { data, error, loading } = useQuery<
    BreederGetBreederQuery,
    BreederGetBreederQueryVariables
  >(BREEDER_GET_BREEDER, {
    variables,
    skip: id == null || id === '',
  });

  const breeder = data?.findManyDogBreeder?.[0];

  const breederFilteredBreeds = useMemo(() => {
    const breeds = breeder?.breederBreeds ?? [];
    const baseBreedNames = breeds.map((x) => x.dogBreed.baseBreedName ?? undefined);
    return breeds.filter((x) => !baseBreedNames.includes(x.dogBreed.name));
  }, [breeder?.breederBreeds]);

  return {
    breeder,
    breederFilteredBreeds,
    breederError: error,
    breederLoading: loading,
    breederVariables: variables,
  };
};
